import React, { useState } from 'react'
import { useField, useFormikContext, Field, FieldArray } from 'formik'
import moment from 'moment'
import { useMutation } from '../../Hooks/graphql_utils'

import {
  FormInput,
  FormSimpleSelect,
  FormSuggest,
  FormSuggestWithCreate,
  FormTextArea,
  MaskedInput,
  PhoneMaskedInput,
  FormMultiSuggest,
  FormEditableText,
  NumberMaskedInput,
  FormMultiSelect,
  FormRadioGroup,
  FormYesNoRadioGroup,
  FormSelect,
  FormDateTimeInput,
  CurrencyMaskedInput,
  FormCustomDateTimePicker,
  FormDate,
} from '../Input'

import { Button, CancelButton } from '../Button'
import { Card } from '../Card'
import { H6 } from '../Text'
import { Classes, Dialog, Intent } from '@blueprintjs/core'
import { GET_PRESIGN } from '../../Components/DocumentTree/queries'
import { putInS3 } from '../../utils/helpers'
import { Spinner } from '../Loader'


export const useFormik = (name) => {
  const { isSubmitting, setFieldValue, submitCount } = useFormikContext()
  const [field, meta] = useField(name)

  const { error, touched } = meta

  return {
    field,
    meta,
    isSubmitting,
    setFieldValue,
    error: (touched || submitCount > 0) && error
  }
}

export const FormikDate = ({ name, dependentName, dependentValueCalculation, listenerComponents = [], noFastField = false, ...props }) => {
  const { field, isSubmitting, error, setFieldValue } = useFormik(name)

  const customOnChange = (e) => {
  }

  const customOnBlur = (e) => {
    const value = e && e.currentTarget ? e.currentTarget.value : e

    let date = moment(value, ['M/D/YYYY', 'M/D/YY', 'YYYY-M-D', 'YY-M-D'], true)
    let fieldDate = field.value ? moment(field.value) : null

    if ((fieldDate !== null && date.format('YYYY-MM-DD') === fieldDate.format('YYYY-MM-DD')) || (field.value === undefined && value === '')) {
      return
    }

    if (dependentName) {
      const newValueForDependent = dependentValueCalculation && (value || value === 0)
        ? dependentValueCalculation(value)
        : null
      setFieldValue(dependentName, newValueForDependent)
    }

    if (listenerComponents && Array.isArray(listenerComponents) && listenerComponents.length > 0) {
      listenerComponents.forEach(lc => {
        if (lc.componentName && lc.componentNewValueCalculator) {
          const newValueForComponent = lc.componentNewValueCalculator(name, value, lc.supportingProps || {})
          setFieldValue(lc.componentName, newValueForComponent)
        }
      })
    }

    if (date.isValid()) {
      setFieldValue(name, date.format('YYYY-MM-DD'))
    } else if (value === '' || value === null) {
      setFieldValue(name, null)
    } else {
      return
    }
  }

  return (
    <FormDate
      {...props}
      {...field}
      disabled={isSubmitting || props.disabled}
      errorText={error}
      onChange={customOnChange}
      value={field.value}
      onBlur={customOnBlur}
    />
  )
}

export const FormikInput = ({ name, dependentName, dependentValueCalculation, listenerComponents = [], noFastField = false, ...props }) => {
  const { field, isSubmitting, error, setFieldValue } = useFormik(name)
  const [internalValue, setInternalValue] = useState(field.value || null)

  //this is so that number fields will not be left with a blank string value when focused
  //without having data entered and consequently unable to be parsed on submission
  const isOfTypeNumber = props.type === 'number'
  const getParsedNumberValue = (e) => e?.currentTarget?.value?.trim() === ''
    ? undefined
    : e?.currentTarget?.value

  const customOnChange = (e) => {
    const parsedValue = isOfTypeNumber
      ? getParsedNumberValue(e)
      : e?.currentTarget?.value

    setInternalValue(parsedValue)
    if (noFastField) {
      setFieldValue(name, parsedValue)
    }
    if (dependentName) {
      const newValueForDependent = dependentValueCalculation && (e.currentTarget.value || e.currentTarget.value === 0)
        ? dependentValueCalculation(e.currentTarget.value)
        : null
      setFieldValue(dependentName, newValueForDependent)
    }
    if (listenerComponents && Array.isArray(listenerComponents) && listenerComponents.length > 0) {
      listenerComponents.forEach(lc => {
        if (lc.componentName && lc.componentNewValueCalculator) {
          const newValueForComponent = lc.componentNewValueCalculator(name, e.currentTarget.value, lc.supportingProps || {})
          setFieldValue(lc.componentName, newValueForComponent)
        }
      })
    }
  }

  const customOnBlur = (e) => {
    const parsedValue = isOfTypeNumber
      ? getParsedNumberValue(e)
      : e?.currentTarget?.value
    setInternalValue(parsedValue)
    setFieldValue(name, parsedValue)
  }

  return (
    <FormInput
      {...props}
      {...field}
      disabled={isSubmitting || props.disabled}
      errorText={error}
      onChange={customOnChange}
      value={noFastField ? field.value : internalValue}
      onBlur={customOnBlur}
      type={isOfTypeNumber ? "number" : props.type}
    />
  )
}

export const FormikRadioGroup = ({ name, dependantNames, ...props }) => {
  const { field, isSubmitting, setFieldValue, error } = useFormik(name)
  return (
    <FormRadioGroup
      {...props}
      {...field}
      items={props.items}
      disabled={isSubmitting || props.disabled}
      errorText={error}
      onChange={(e) => {
        const val = props.items.find(i => i.id === +e.currentTarget.value)
        if (dependantNames && dependantNames.length > 0) {
          dependantNames.forEach(d => setFieldValue(d, null))
        }

        if (props.sideEffects && props.sideEffects.length) {
          props.sideEffects.forEach(se => {
            if (se?.conditional && se.conditional(val) && se?.target && se?.newValueCalculator) {
              setFieldValue(se.target, se.newValueCalculator(val))
            }
          })
        }

        return setFieldValue(name, val)
      }}
    />
  )
}

export const FormikYesNoRadioGroup = ({ name, dependantNames, ...props }) => {
  const { field, isSubmitting, setFieldValue, error } = useFormik(name)

  const items = [
    { id: 1, name: 'Yes' },
    { id: 0, name: 'No' },
  ]

  return (
    <FormYesNoRadioGroup
      {...props}
      {...field}
      items={items}
      disabled={isSubmitting || props.disabled}
      errorText={error}
      onChange={(e) => {
        const val = items.find(i => i.id === +e.currentTarget.value)
        
        if (dependantNames && dependantNames.length > 0) {
          dependantNames.forEach(d => setFieldValue(d, null))
        }

        return setFieldValue(name, val.id === 1)
      }}
    />
  )
}

export const FormikEditableText = ({ name, ...props }) => {
  const { field, isSubmitting, setFieldValue, error } = useFormik(name)
  return (
    <FormEditableText
      {...props}
      {...field}
      disabled={isSubmitting || props.disabled}
      errorText={error}
      onChange={(item) => { return setFieldValue(name, item) }}
    />
  )
}

export const FormikMaskedInput = ({ name, ...props }) => {
  const { field, isSubmitting, error } = useFormik(name)

  return (
    <MaskedInput
      {...props}
      {...field}
      disabled={isSubmitting || props.disabled}
      errorText={error}
    />
  )
}

export const FormikPhoneMaskedInput = ({ name, ...props }) => {
  const { field, isSubmitting, error } = useFormik(name)

  return (
    <PhoneMaskedInput
      {...props}
      {...field}
      disabled={isSubmitting || props.disabled}
      errorText={error}
    />
  )
}

export const FormikNumberMaskedInput = ({ name, type, ...props }) => {
  let { field, isSubmitting, setFieldValue, error } = useFormik(name)

  return (
    <NumberMaskedInput
      {...props}
      {...field}
      disabled={isSubmitting || props.disabled}
      errorText={error}
      onChange={async (e) => {
        const val = e.currentTarget.value
        const intVal = type === 'number' ? parseInt(val.replace(/\D+/g, '')) : null
        setFieldValue(name, type === 'number'
          ? (!isNaN(intVal) ? intVal : null)
          : val)
      }
      }
    />
  )
}
export const FormikCurrencyMaskedInput = ({ name, type, style, ...props }) => {
  let { field, isSubmitting, setFieldValue, error } = useFormik(name)

  return (
    <CurrencyMaskedInput
      {...props}
      {...field}
      disabled={isSubmitting || props.disabled}
      errorText={error}
      onChange={async (e) => {
        const val = e.currentTarget.value
        setFieldValue(name, type === 'currency' ? ((val.replace(/[^0-9\\.?]/g, '') || 0)) : val)
      }
      }
      style={style}
    />
  )
}
export const FormikTextArea = ({ name, ...props }) => {

  const { field, isSubmitting, error, setFieldValue } = useFormik(name)
  const [internalValue, setInternalValue] = useState(field.value || null)
  const customOnChange = (e) => {
    setInternalValue(e.currentTarget.value)
  }
  const customOnBlur = (e) => {
    setInternalValue(e.currentTarget.value)
    setFieldValue(name, e.currentTarget.value)
  }

  return (
    <FormTextArea
      {...props}
      {...field}
      onChange={customOnChange}
      onBlur={customOnBlur}
      value={internalValue}
      disabled={isSubmitting || props.disabled}
      errorText={error}
    />
  )
}


export const FormikSuggestWithCreate = ({ name, value, disableMaxListSize, ...props }) => {
  const { error, field, isSubmitting, setFieldValue } = useFormik(name)

  return (
    <FormSuggestWithCreate
      {...props}
      value={value && value.name ? value.name : field.value}
      onBlur={field.onBlur}
      onChange={(item) => {
        return setFieldValue(field.name, (item && item.name) || null)
      }}
      name={field.name}
      disabled={isSubmitting || props.disabled}
      errorText={error}
      disableMaxListSize={disableMaxListSize}
    />
  )
}

export const FormikSuggest = ({ name, value, dependentName, dependent, updateDependent, onChange, ...props }) => {
  const { error, field, isSubmitting, setFieldValue } = useFormik(name)
  const dForm = useFormik(dependent)
  return (
    <FormSuggest
      {...props}
      value={value ? value : field.value}
      onBlur={field.onBlur}
      onChange={(item) => {
        if (onChange) {
          onChange(item)
        }
        if (dependent) {
          updateDependent(item, dependent, (val) => {
            setFieldValue(dForm.field.name, val)
          })
        }
        return setFieldValue(field.name, item)
      }}
      name={field.name}
      disabled={isSubmitting || props.disabled}
      errorText={error}
    />
  )
}

export const FormikMultiSuggest = ({ name, ...props }) => {
  const { error, field, isSubmitting, setFieldValue } = useFormik(name)

  return (
    <FormMultiSuggest
      {...props}
      value={field.value}
      onBlur={field.onBlur}
      onChange={(item) => setFieldValue(field.name, item)}
      name={field.name}
      disabled={isSubmitting || props.disabled}
      errorText={error}
    />
  )
}

export const FormikSelect = ({ name, listenerComponents = [], ...props }) => {
  const { field, isSubmitting, error, setFieldValue } = useFormik(name)
  const handledError = !error || typeof error === 'string' ? error : false

  return (
    <FormSelect
      {...props}
      {...field}
      onChange={(item) => {
        setFieldValue(name, item)
        if (listenerComponents && Array.isArray(listenerComponents) && listenerComponents.length > 0) {
          listenerComponents.forEach(lc => {
            if (lc.componentName && lc.componentNewValueCalculator) {
              const newValueForComponent = lc.componentNewValueCalculator(name, item, lc.supportingProps || {})
              setFieldValue(lc.componentName, newValueForComponent)
            }
          })
        }
        if (props.onChange) {
          props.onChange(item)
        }
      }}
      disabled={isSubmitting || props.disabled}
      errorText={handledError}
    />
  )
}

export const FormikSimpleSelect = ({ name, dependentName, listenerComponents = [], ...props }) => {
  const { field, isSubmitting, error, setFieldValue } = useFormik(name)
  const dForm = useFormik(dependentName || `dep_${name}`)


  return (
    <FormSimpleSelect
      {...props}
      {...field}
      onChange={(item) => {
        if (dependentName) {
          if (Array.isArray(dependentName)) {
            dependentName.forEach(d => setFieldValue(d, null))
          } else {
            dForm.setFieldValue(dForm.field.name, props.nullable ? null : '')
          }
        }

        if (listenerComponents && Array.isArray(listenerComponents) && listenerComponents.length > 0) {
          listenerComponents.forEach(lc => {
            if (lc.componentName && lc.componentNewValueCalculator) {
              const newValueForComponent = lc.componentNewValueCalculator(name, item, lc.supportingProps || {})
              setFieldValue(lc.componentName, newValueForComponent)
            }
          })
        }

        setFieldValue(name, item)
        if (props.onChange) {
          props.onChange(item)
        }
      }}
      disabled={isSubmitting || props.disabled}
      errorText={error}
    />
  )
}

export const FormikMultiSelect = ({ name, listenerComponents, ...props }) => {
  const { field, isSubmitting, error, setFieldValue } = useFormik(name)

  const customOnChange = (value) => {

    setFieldValue(name, value)

    if (listenerComponents && Array.isArray(listenerComponents) && listenerComponents.length > 0) {
      listenerComponents.forEach(lc => {
        if (lc.componentName && lc.componentNewValueCalculator) {
          const newValueForComponent = lc.componentNewValueCalculator(name, value, lc.supportingProps || {})
          setFieldValue(lc.componentName, newValueForComponent)
        }
      })
    }
  }

  return (
    <FormMultiSelect
      {...props}
      {...field}
      onChange={(value) => customOnChange(value)}
      disabled={isSubmitting || props.disabled}
      errorText={error}
    />
  )
}

export const FormikCheckbox = ({ name, dependentName, dependentsNewValue, checked, label, style = {}, ...props }) => {
  const { setFieldValue } = useFormik(name)
  const dForm = useFormik(dependentName || `dep_${name}`)

  const divStyle = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
    ...style
  }
  const customOnChange = () => {
    if (dependentName) {
      if (Array.isArray(dependentName)) {
        dependentName.forEach(d => dForm.setFieldValue(d, dependentsNewValue ?? null))
      } else {
        dForm.setFieldValue(dForm.field.name, dependentsNewValue ?? null)
      }
    }
    setFieldValue(name, !checked)
  }
  return (
    <div style={divStyle}>
      <Field
        style={{ marginTop: 3, marginRight: 10 }}
        name={name}
        checked={checked}
        type='checkbox'
        onChange={customOnChange}
      />
      <p style={{ marginTop: 0, fontWeight: 400 }}>{label}</p>
    </div>
  )
}

const boxStyle = {
  "border": "solid 1px #000",
  "width": "150px",
  "height": "200px",
  "textAlign": "center",
  "lineHeight": '180px'
}

var reader = new FileReader();

export const FormikFileInput = ({ name, value, buttonText, accept, width = 150, title, innerRef, fileKey, uploadUrl, ...props }) => {
  const [image_uri, setImageUri] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [getPresign] = useMutation(GET_PRESIGN)

  const [isModalOpen, setIsModalOpen] = useState(false)

  reader.onload = function (e) {
    setImageUri(e.target.result)
  }

  return (
    <>
      <div>
        {(image_uri || value) &&
          <img src={image_uri || value} width={width} alt={'profile'} />
        }
        {!(image_uri || value) && <div style={boxStyle}>No image</div>}
      </div>

      <Button
        intent={Intent.SUCCESS}
        text={'Upload'}
        onClick={() => {
          setIsModalOpen(true)
        }
        }
      />

      <Dialog
        title='Upload image'
        isOpen={isModalOpen}
        canOutsideClickClose={false}
        isCloseButtonShown={false}
      >
        <div className={Classes.DIALOG_BODY}>
          <Card>
            <input
              id={name}
              name={name}
              type="file"
              accept={accept}
              title={title}
              text={buttonText}
              onChange={async (event) => {
                if (props.onChange) {
                  props.onChange(event)
                } else {
                  if (event.currentTarget.files[0] && uploadUrl) {
                    let file = event.currentTarget.files[0]
                    try {
                      setLoading(true)
                      const presignResponse = await getPresign({
                        variables: {
                          key: fileKey,
                          contentType: file.type
                        }
                      })

                      await putInS3(presignResponse.data.presign.url, file)
                      await fetch(uploadUrl, { method: "PUT" }).then(() => {
                        setLoading(false)
                        setIsModalOpen(false)
                        reader.readAsDataURL(file);
                      })

                    } catch (ex) {
                      setError('Error uploading image. Please try again.')
                      setLoading(false)
                    }



                  }
                }
              }}
              ref={innerRef}
            />
            {loading && <div><Spinner size={20} /> <span>Uploading file...</span></div>}
            {error && <div>{error}</div>}

          </Card>
          <CancelButton onClick={() => setIsModalOpen(false)} />
        </div>

      </Dialog>

    </>
  )
}

export const FormikCheckboxArray = ({ name, value = [], label, items, ...props }) => {
  const style = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline'
  }
  return (
    <FieldArray
      name={name}
      render={({ push, remove }) => (
        <>
          <H6>{label}</H6>
          {items.map(item => {
            let indexOfAlreadySelectedItem = value.map(v => v.id).indexOf(item.id)
            let itemAlreadySelected = indexOfAlreadySelectedItem !== -1
            return (
              <div style={style} key={`${name}_${item.id}`}>
                <Field
                  name={name}
                  type='checkbox'
                  checked={itemAlreadySelected}
                  style={{ marginTop: 3, marginRight: 10 }}
                  onChange={() => {
                    if (!itemAlreadySelected) {
                      push(item)
                    }
                    else {
                      remove(indexOfAlreadySelectedItem)
                    }
                  }}
                />
                <p style={{ marginTop: 0, fontWeight: 400 }}>{item.name || 'Item Name Not Found'}</p>
              </div>
            )
          })}
        </>)}
    />
  )
}

export const FormikDateTimeInput = ({ name, ...props }) => {
  const { field, isSubmitting, error, setFieldValue } = useFormik(name)
  return (
    <FormDateTimeInput
      {...props}
      {...field}
      disabled={isSubmitting || props.disabled}
      errorText={error}
      onChange={e => setFieldValue(name, e)}
    />
  )
}

export const FormikCustomDateTimePicker = ({ name, dependentName, dependentValueCalculation, ...props }) => {
  const { field, isSubmitting, error, setFieldValue } = useFormik(name)

  return (
    <FormCustomDateTimePicker
      {...props}
      {...field}
      disabled={isSubmitting || props.disabled}
      errorText={error}
      onTextChange={e => {
        setFieldValue(name, e.currentTarget.value)
      }}
      onDateTimeChange={e => {
        setFieldValue(name, e)
        if (dependentName) {
          const newValueForDependent = dependentValueCalculation && (e || e === 0)
            ? dependentValueCalculation(e)
            : null
          setFieldValue(dependentName, newValueForDependent)
        }
      }}
      onBlur={field.onBlur}
    />
  )
}
